import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { PublicThemeToggle } from '@/components/ui/PublicThemeToggle';
import usePageMeta from '@/hooks/usePageMeta';
import CapacityStory from '@/components/marketing/CapacityStory';
import { SEO_ROUTES } from '@/src/seo/routes';
import MarketingHeader from '@/components/marketing/MarketingHeader';
import MarketingFooter from '@/components/marketing/MarketingFooter';
import AudienceDoors from '@/components/marketing/AudienceDoors';
import OneEncounter from '@/components/marketing/OneEncounter';
import FunderDiscovery from '@/components/marketing/FunderDiscovery';
import ProofStrip from '@/components/marketing/ProofStrip';
import { ENGAGEMENT, ENGAGEMENT_MULTIPLE, MEMBERS_SERVED, PROOF_STRIP } from '@/components/marketing/siteFacts';
import {
  ArrowRight,
  Users,
  Building,
  Zap,
  BarChart3,
  Target,
  TrendingUp,
  TrendingDown,
  Plus,
  LogIn,
  Heart,
  DollarSign,
  Shield,
  Clock,
  CheckCircle,
  Building2,
  Activity,
  Menu,
  X,
  Home,
  Apple,
  Car,
  Briefcase,
  GraduationCap,
  HelpingHand,
  HeartPulse,
  ShieldCheck,
  Network,
  Quote,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  HeartHandshake,
  MapPin
} from 'lucide-react';

interface HomepageProps {
  onNavigateToDirectory: () => void;
  onNavigateToLiveFeed: () => void;
  onNavigateToMatchingEngine: () => void;
  onNavigateToCapacity: () => void;
  onNavigateToSubmissions: () => void;
  onCreateNew: () => void;
  onNavigateToFinder: () => void;
  onNavigateToQueue: () => void;
  onNavigateToLogin: () => void;
  onNavigateToSignup?: () => void;
  onNavigateToRailwayTest?: () => void;
  totalReferrals?: number;
  totalProviders?: number;
  autoMatchedCount?: number;
}

const API_BASE = import.meta.env.VITE_API_URL ?? '';

/**
 * The hero rotates through what WeaveIn actually spans — enrollment, care
 * coordination, service delivery, running several programs at once, and
 * documenting the work so it can be billed. One use case in the hero made the
 * product look like a referral tool.
 *
 * Each line is a person's situation first and the product second, which is the
 * register the Impact4Health note asked for.
 */
/**
 * The hero's photographs. Documentary, not illustration, per the design
 * system — real people in real places.
 *
 * `rural` is here because the people this platform reaches are not all in
 * cities, and the hero only ever showed an indoor family. `community` is a
 * portrait triptych, so the landscape frame crops it to roughly its middle
 * panel; it is also 708px wide for a slot that renders at 511 and wants twice
 * that on a retina screen. It stays until there is a landscape photo to
 * replace it with.
 */
const HERO_IMAGES = {
  family: { src: '/images/hero-family.jpg', alt: 'A family supported by community health services' },
  rural: {
    src: '/images/hero-rural-family.jpg',
    alt: 'Three generations of a farming family beside a truck on their land',
  },
  community: { src: '/images/Component 1.png', alt: 'Community members served by WeaveIn partners' },
} as const;

type HeroImageKey = keyof typeof HERO_IMAGES;

const HERO_MESSAGES = [
  {
    label: 'Referral',
    image: 'family' as HeroImageKey,
    text: 'A patient leaves the hospital needing food, housing and a ride to their next appointment. WeaveIn is how the nurse who discharged them connects those services\u2014and how she finds out they actually arrived.',
  },
  {
    label: 'Enrollment',
    image: 'rural' as HeroImageKey,
    text: 'A family qualifies for coverage and does not know it. WeaveIn is how a community worker reaches them, enrolls them in their own language, and keeps the renewal from lapsing.',
  },
  {
    label: 'Care',
    image: 'community' as HeroImageKey,
    text: 'A member with complex needs has a care manager, a housing navigator and a clinic that never speak. WeaveIn is where Enhanced Care Management and Community Supports run on one record.',
  },
  {
    label: 'Programs',
    image: 'rural' as HeroImageKey,
    text: 'A community organization runs six programs for four funders, each with its own forms and rules. WeaveIn is how one team runs all of them without six systems.',
  },
  {
    label: 'Payment',
    image: 'family' as HeroImageKey,
    text: 'The care was delivered. Proving it is what gets it paid. WeaveIn documents the encounter against the right program and that plan\u2019s own rules, so the work can be billed rather than absorbed.',
  },
];

const Homepage: React.FC<HomepageProps> = ({
  onNavigateToDirectory,
  onNavigateToLogin,
  onNavigateToSignup,
}) => {
  usePageMeta(SEO_ROUTES['/']);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [heroMessage, setHeroMessage] = useState(0);
  const [providerCount, setProviderCount] = useState('1.7M+');
  const [providerCountNum, setProviderCountNum] = useState('1,700,000+');

  useEffect(() => {
    fetch('/provider-api/stats')
      .then(res => res.json())
      .then(data => {
        if (data.total_providers) {
          setProviderCount(data.total_providers >= 1000000 ? (data.total_providers / 1000000).toFixed(1) + 'M+' : Math.floor(data.total_providers / 1000).toLocaleString() + 'K+');
          setProviderCountNum(data.total_providers.toLocaleString() + '+');
        }
      })
      .catch(() => {
        fetch(`${API_BASE}/api/public/network-stats`)
          .then(res => res.json())
          .then(data => {
            if (data.totalProviders) {
              setProviderCount(data.totalProviders >= 1000000 ? (data.totalProviders / 1000000).toFixed(1) + 'M+' : Math.floor(data.totalProviders / 1000).toLocaleString() + 'K+');
              setProviderCountNum(data.totalProviders.toLocaleString() + '+');
            }
          })
          .catch(() => {});
      });
  }, []);

  useEffect(() => {
    // Someone who has asked for less motion gets the first message and a still
    // image rather than a hero that rewrites itself while they are reading it.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const interval = setInterval(() => {
      setHeroMessage((prev) => (prev + 1) % HERO_MESSAGES.length);
    }, 7000);
    return () => clearInterval(interval);
  }, []);

  const testimonials = [
    {
      quote: "This platform lets us build our reputation in the community and helps connect with people reliably.",
      name: "David Godinez",
      title: "Project and Outreach Coordinator",
      org: "CALCHI"
    },
    {
      quote: "Trust-WeaveIn has empowered us to connect with community members and maximized our reach.",
      name: "Kimberly Martinez",
      title: "Program Associate",
      org: "Alliance for a Better Community"
    },
    {
      quote: "System integrity, ease of creating engagements, and excellent customer service have been exceptional.",
      name: "Amira Lindbloom",
      title: "Director of Care Coordination",
      org: "Peach Tree Healthcare"
    },
    {
      quote: "Within 10 minutes our phones were lighting up and it drove traffic to our website.",
      name: "Amalia Chandley",
      title: "Director",
      org: "Central Health Access Assisters"
    },
    {
      quote: "The Trust-WeaveIn team has acted as an extension of our team.",
      name: "Mark Diel",
      title: "CEO",
      org: "California Coverage & Health Initiatives"
    },
    {
      quote: "This experience solidified the power of the platform for connecting members to care.",
      name: "Case Manager",
      title: "Mental Health Services",
      org: "Enhanced Care Provider"
    }
  ];

  const partnerOrgs = [
    { name: "CALCHI", logo: "/images/logos/calchi.svg" },
    { name: "Alliance for a Better Community", logo: "/images/logos/alliance-better-community.svg" },
    { name: "Peach Tree Healthcare", logo: "/images/logos/peach-tree-health.svg" },
    { name: "Central Health Access Assisters", logo: "/images/logos/central-health-access.svg" },
    { name: "HealthNet", logo: "/images/logos/healthnet.svg" },
    { name: "CCHI", logo: "/images/logos/cchi.svg" }
  ];

  const nextTestimonial = () => setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
  const prevTestimonial = () => setActiveTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      {/* Navigation Header */}
      <MarketingHeader onLogin={onNavigateToLogin} />

      {/* ================================================================ */}
      {/* HERO SECTION - Split Layout with Photo                          */}
      {/* ================================================================ */}
      <section className="bg-paper py-12 md:py-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center lg:items-stretch">
            {/* Left: Messaging */}
            <div>
              <div className="inline-flex items-center bg-v2accent-soft text-ink text-sm font-semibold px-4 py-2 rounded-full mb-6">
                <Zap className="w-4 h-4 mr-2" />
                Medicaid plans, counties and grants, on one record
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-ink mb-6 leading-tight">
                One ledger.<br />
                <span className="text-v2accent">Every funder.</span>
              </h1>
              <p className="text-lg text-gray-700 dark:text-gray-200 mb-6 max-w-xl">
                WeaveIn sits between community organizations and the plans, counties and grants that pay them. A worker
                captures the visit once; agents handle each funder&rsquo;s eligibility, submission and payment matching, and a
                named person approves every step.
              </p>
              <div className="mb-8">
                <div className="flex flex-wrap gap-2 mb-4" role="tablist" aria-label="What WeaveIn does">
                  {HERO_MESSAGES.map((m, i) => (
                    <button
                      key={m.label}
                      role="tab"
                      aria-selected={i === heroMessage}
                      onClick={() => setHeroMessage(i)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold tracking-wide transition-colors ${
 i === heroMessage
 ? 'bg-ink text-paper'
 : 'bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-800 hover:border-v2accent hover:text-ink'
 }`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
                <div className="bg-v2accent-soft border border-v2accent rounded-xl p-5 md:p-6 md:min-h-[10rem] flex items-center">
                  <p
                    key={heroMessage}
                    className="text-xl md:text-2xl text-gray-800 dark:text-gray-100 leading-snug animate-in fade-in duration-500"
                  >
                    {HERO_MESSAGES[heroMessage].text}
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center rounded-lg bg-ink hover:bg-ink-700 text-paper px-8 py-4 text-lg font-semibold shadow-lg transition-colors"
                >
                  Talk to us
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
                <Link
                  to="/platform"
                  className="inline-flex items-center justify-center rounded-lg border-2 border-ink text-ink hover:bg-ink hover:text-paper px-8 py-4 text-lg font-semibold transition-colors"
                >
                  See how it works
                </Link>
              </div>
            </div>

            {/* Right: Hero Image Carousel */}
            <div className="relative flex">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl w-full h-[300px] sm:h-[380px] md:h-[430px] lg:h-auto lg:min-h-[520px]">
                {(Object.keys(HERO_IMAGES) as HeroImageKey[]).map((key) => (
                  <img
                    key={key}
                    src={HERO_IMAGES[key].src}
                    alt={HERO_IMAGES[key].alt}
                    loading={key === HERO_MESSAGES[0].image ? 'eager' : 'lazy'}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
 HERO_MESSAGES[heroMessage].image === key ? 'opacity-100' : 'opacity-0'
 }`}
                  />
                ))}
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
              {/* Floating stat cards */}
              <div className="absolute -bottom-4 -left-4 bg-white dark:bg-gray-900 rounded-xl shadow-lg p-4 border border-gray-100 dark:border-gray-800">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-v2accent-soft rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-v2accent" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-ink">{MEMBERS_SERVED.value}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{MEMBERS_SERVED.label}</div>
                  </div>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 bg-white dark:bg-gray-900 rounded-xl shadow-lg p-4 border border-gray-100 dark:border-gray-800">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-v2accent-soft rounded-lg flex items-center justify-center">
                    <Activity className="w-5 h-5 text-ink" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-v2accent">{providerCount}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Providers in the directory</div>
                    <div className="text-[10px] leading-tight text-gray-400 dark:text-gray-500 mt-0.5 max-w-[150px]">
                      Searchable U.S. health &amp; social services
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================ */}
      {/* TRUSTED BY - Partner Logo Strip                                 */}
      {/* ================================================================ */}
      <section className="py-8 bg-gray-50 dark:bg-gray-950 border-y border-gray-100 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-6">
            Trusted by leading organizations
          </p>
          <div className="flex flex-wrap justify-center items-center gap-x-8 gap-y-6">
            {partnerOrgs.map((org) => (
              <div
                key={org.name}
                className="grayscale hover:grayscale-0 opacity-60 hover:opacity-100 transition-all duration-300"
              >
                <img
                  src={org.logo}
                  alt={org.name}
                  className="h-12 md:h-14 w-auto max-w-[180px] object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                    if (fallback) fallback.classList.remove('hidden');
                  }}
                />
                <span className="hidden text-gray-400 dark:text-gray-500 font-semibold text-sm whitespace-nowrap">{org.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================================================================ */}
      {/* THREE DOORS, THEN ONE ENCOUNTER ACROSS FOUR FUNDERS             */}
      {/* ================================================================ */}
      <AudienceDoors />
      <OneEncounter />
      <FunderDiscovery />

      {/* ================================================================ */}
      {/* HOW IT WORKS - Connected Process Flow                           */}
      {/* ================================================================ */}
      <section className="py-20 bg-white dark:bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-ink mb-4">
              How WeaveIn Works
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              One record, from the first contact to the service delivered&mdash;and the
              documentation that gets the work paid for.
            </p>
          </div>

          <div className="relative">
            {/* Connection line - desktop only */}
            <div className="hidden lg:block absolute top-1/2 left-[12.5%] right-[12.5%] h-1 bg-v2border -translate-y-1/2 rounded-full z-0"></div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 relative z-10">
              <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg text-center border-2 border-transparent hover:border-v2accent transition-all">
                <div className="w-20 h-20 bg-v2accent rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <Target className="w-10 h-10 text-white" />
                </div>
                <div className="inline-flex items-center bg-v2accent-soft text-v2accent font-bold text-xs px-3 py-1 rounded-full mb-3">STEP 1</div>
                <h3 className="text-xl font-bold text-ink mb-4">Find and enroll</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Identify who needs help and who already qualifies for coverage. Reach them
                  in their own language, on the device they already carry, and enroll them.
                </p>
              </div>

              <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg text-center border-2 border-transparent hover:border-v2accent transition-all">
                <div className="w-20 h-20 bg-v2accent rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <Users className="w-10 h-10 text-white" />
                </div>
                <div className="inline-flex items-center bg-v2accent-soft text-v2accent font-bold text-xs px-3 py-1 rounded-full mb-3">STEP 2</div>
                <h3 className="text-xl font-bold text-ink mb-4">Coordinate and deliver</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Route to a community organization that is near them and able to help, from a
                  directory of {providerCount} providers. Run Enhanced Care Management,
                  Community Supports and your own programs on the same record.
                </p>
              </div>

              <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg text-center border-2 border-transparent hover:border-v2accent transition-all">
                <div className="w-20 h-20 bg-v2accent rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <BarChart3 className="w-10 h-10 text-white" />
                </div>
                <div className="inline-flex items-center bg-v2accent-soft text-v2accent font-bold text-xs px-3 py-1 rounded-full mb-3">STEP 3</div>
                <h3 className="text-xl font-bold text-ink mb-4">Confirm and document</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Capture what was delivered, send confirmation back to whoever referred, and
                  document it against the right program and that plan&rsquo;s own rules.
                </p>
              </div>

              <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg text-center border-2 border-transparent hover:border-v2accent transition-all">
                <div className="w-20 h-20 bg-v2accent rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <DollarSign className="w-10 h-10 text-white" />
                </div>
                <div className="inline-flex items-center bg-v2accent-soft text-v2accent font-bold text-xs px-3 py-1 rounded-full mb-3">STEP 4</div>
                <h3 className="text-xl font-bold text-ink mb-4">Bill it without the rework</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Before anything is sent, the claim is checked against that plan&rsquo;s menu
                  and rates and against the national edit rules&mdash;so the work you already
                  did comes back paid, not denied.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================ */}
      {/* PLATFORM IN ACTION - Product Screenshots                        */}
      {/* ================================================================ */}
      <section className="py-20 bg-gray-50 dark:bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-ink mb-4">
              See the Platform in Action
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              From workflow automation to referral tracking to retention intelligence—all in one place.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Screenshot 1: Workflow */}
            <div className="group cursor-pointer transition-transform duration-300 hover:scale-150 hover:z-10 relative">
              <div className="rounded-xl overflow-hidden shadow-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 group-hover:shadow-2xl transition-shadow duration-300">
                <img
                  src="/images/screenshot-workflows.png"
                  alt="Activities Workflows - Medicaid Enrollment & Work Compliance"
                  className="w-full h-auto"
                />
              </div>
              <div className="mt-4 text-center">
                <h4 className="text-lg font-bold text-ink">Workflow Automation</h4>
                <p className="text-base text-gray-600 dark:text-gray-300">Step-by-step compliance and enrollment workflows</p>
              </div>
            </div>

            {/* Screenshot 2: Referral Journey */}
            <div className="group cursor-pointer transition-transform duration-300 hover:scale-150 hover:z-10 relative">
              <div className="rounded-xl overflow-hidden shadow-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 group-hover:shadow-2xl transition-shadow duration-300">
                <img
                  src="/images/screenshot-referral.png"
                  alt="Service Referral Journey - Consent to Completion"
                  className="w-full h-auto"
                />
              </div>
              <div className="mt-4 text-center">
                <h4 className="text-lg font-bold text-ink">Referral Journey</h4>
                <p className="text-base text-gray-600 dark:text-gray-300">End-to-end visibility from consent to completion</p>
              </div>
            </div>

            {/* Screenshot 3: Retention Intelligence */}
            <div className="group cursor-pointer transition-transform duration-300 hover:scale-150 hover:z-10 relative">
              <div className="rounded-xl overflow-hidden shadow-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 group-hover:shadow-2xl transition-shadow duration-300">
                <img
                  src="/images/screenshot-retention.png"
                  alt="Member Retention Intelligence Dashboard"
                  className="w-full h-auto"
                />
              </div>
              <div className="mt-4 text-center">
                <h4 className="text-lg font-bold text-ink">Retention &amp; Engagement</h4>
                <p className="text-base text-gray-600 dark:text-gray-300">See who has not been reached this month, and reach them</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================ */}
      {/* PROOF — the seed deck's numbers, from siteFacts                 */}
      {/* ================================================================ */}
      <ProofStrip
        tone="dark"
        metrics={[
          ENGAGEMENT,
          ENGAGEMENT_MULTIPLE,
          MEMBERS_SERVED,
          ...PROOF_STRIP.filter((m) => m.label !== 'Members in cohort').slice(0, 3),
        ]}
      />

      {/* ================================================================ */}
      {/* TESTIMONIALS - Carousel                                         */}
      {/* ================================================================ */}
      <section className="py-20 bg-white dark:bg-gray-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-ink mb-4">
              What Our Partners Say
            </h2>
          </div>

          <div className="relative">
            <Card className="border-0 shadow-xl bg-paper-2">
              <CardContent className="p-8 md:p-12">
                <Quote className="w-10 h-10 text-v2accent/40 mb-6" />
                <blockquote className="text-xl md:text-2xl text-gray-700 dark:text-gray-200 leading-relaxed mb-8 min-h-[80px]">
                  &ldquo;{testimonials[activeTestimonial].quote}&rdquo;
                </blockquote>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-bold text-ink text-lg">
                      {testimonials[activeTestimonial].name}
                    </div>
                    <div className="text-gray-500 dark:text-gray-400">
                      {testimonials[activeTestimonial].title}
                    </div>
                    <div className="text-v2accent font-medium">
                      {testimonials[activeTestimonial].org}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={prevTestimonial}
                      className="w-10 h-10 rounded-full border-2 border-v2border flex items-center justify-center hover:bg-ink hover:text-paper hover:border-v2accent transition-all text-ink"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={nextTestimonial}
                      className="w-10 h-10 rounded-full border-2 border-v2border flex items-center justify-center hover:bg-ink hover:text-paper hover:border-v2accent transition-all text-ink"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                {/* Dots */}
                <div className="flex justify-center space-x-2 mt-6">
                  {testimonials.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveTestimonial(i)}
                      className={`w-2.5 h-2.5 rounded-full transition-all ${
 i === activeTestimonial ? 'bg-v2accent w-6' : 'bg-gray-300 hover:bg-gray-400'
 }`}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ================================================================ */}
      {/* TRUST INDICATORS                                                */}
      {/* ================================================================ */}
      <section className="py-16 bg-white dark:bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-v2accent-soft rounded-2xl flex items-center justify-center mb-4">
                <Shield className="w-8 h-8 text-ink" />
              </div>
              <h4 className="font-bold text-gray-800 dark:text-gray-100 mb-2">HIPAA Compliant</h4>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                BAAs, tenant isolation and audit logging. <Link to="/security" className="underline">Security</Link>
              </p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-v2accent-soft rounded-2xl flex items-center justify-center mb-4">
                <Clock className="w-8 h-8 text-v2accent" />
              </div>
              <h4 className="font-bold text-gray-800 dark:text-gray-100 mb-2">Pilot Before Scale</h4>
              <p className="text-gray-600 dark:text-gray-300 text-sm">One program and a defined population, measurable before you scale</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-green-50 rounded-2xl flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h4 className="font-bold text-gray-800 dark:text-gray-100 mb-2">Performance-Based</h4>
              <p className="text-gray-600 dark:text-gray-300 text-sm">Pricing options aligned with your outcomes</p>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================ */}
      {/* FINAL CTA                                                       */}
      {/* ================================================================ */}
      <section className="py-20 bg-paper border-b border-v2border" style={{ marginLeft: 'calc(-50vw + 50%)', marginRight: 'calc(-50vw + 50%)', paddingLeft: 'calc(50vw - 50%)', paddingRight: 'calc(50vw - 50%)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-ink mb-6">
            Start the conversation.
          </h2>
          <p className="text-xl text-ink-500 mb-10">
            See how one ledger can carry every funder your organization works with: an intro call, a walkthrough of a live
            encounter from your own programs, then a pilot.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center rounded-lg bg-ink hover:bg-ink-700 text-paper px-8 py-4 text-lg font-semibold shadow-lg transition-colors"
            >
              Talk to us
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
            <Link
              to="/funder-coverage"
              className="inline-flex items-center justify-center rounded-lg border-2 border-ink text-ink hover:bg-ink hover:text-paper px-8 py-4 text-lg font-semibold transition-colors"
            >
              See funder coverage
            </Link>
          </div>
        </div>
      </section>

      {/* ================================================================ */}
      {/* FOOTER                                                          */}
      {/* ================================================================ */}
      <MarketingFooter onLogin={onNavigateToLogin} onProviderDirectory={onNavigateToDirectory} />
    </div>
  );
};

export default Homepage;
